/**
 * Google OAuth authentication helper
 * Uses Google Identity Services (GIS) for OAuth 2.0
 */

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token?: string; error?: string }) => void;
          }) => {
            requestAccessToken: () => void;
          };
          hasGrantedAllScopes: (tokenResponse: any, ...scopes: string[]) => boolean;
        };
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: {
              credential: string;
              select_by?: string;
            }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          disableAutoSelect: () => void;
          prompt: (callback?: (notification: any) => void) => void;
          renderButton: (element: HTMLElement, config: {
            theme?: 'outline' | 'filled_blue' | 'filled_black';
            size?: 'large' | 'medium' | 'small';
            text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
            shape?: 'rectangular' | 'pill' | 'circle' | 'square';
            logo_alignment?: 'left' | 'center';
            width?: number;
            locale?: string;
          }) => void;
        };
      };
    };
  }
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

/**
 * Load Google Identity Services script
 */
export function loadGoogleScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
}

/**
 * Initialize Google Sign-In and get ID token
 */
export async function signInWithGoogle(): Promise<string> {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('Google OAuth is not configured. Please set VITE_GOOGLE_CLIENT_ID.');
  }

  await loadGoogleScript();

  return new Promise((resolve, reject) => {
    if (!window.google?.accounts?.id) {
      reject(new Error('Google Identity Services not loaded'));
      return;
    }

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response) => {
        if (response.credential) {
          resolve(response.credential);
        } else {
          reject(new Error('Failed to get Google credential'));
        }
      },
    });

    // Use One Tap or show button fallback
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        // If One Tap is not shown, we need to trigger it manually or use button
        // For now, we'll just wait - the button should handle it
        console.log('One Tap not displayed, using button fallback');
      }
    });
  });
}

/**
 * Render Google Sign-In button
 */
export async function renderGoogleButton(
  element: HTMLElement,
  callback: (idToken: string) => void,
  options?: {
    theme?: 'outline' | 'filled_blue' | 'filled_black';
    size?: 'large' | 'medium' | 'small';
    text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  }
): Promise<void> {
  if (!GOOGLE_CLIENT_ID) {
    console.warn('Google OAuth is not configured - VITE_GOOGLE_CLIENT_ID is missing');
    return;
  }

  await loadGoogleScript();

  if (!window.google?.accounts?.id) {
    throw new Error('Google Identity Services not loaded');
  }

  // Disable auto-select to prevent cached account selection
  window.google.accounts.id.disableAutoSelect();
  
  // Initialize with callback
  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (response) => {
      if (response.credential) {
        callback(response.credential);
      } else {
        console.error('No credential in Google response');
      }
    },
    auto_select: false, // Disable auto-select
    cancel_on_tap_outside: true, // Allow canceling
  });

  // Render the button
  try {
    window.google.accounts.id.renderButton(element, {
      theme: options?.theme || 'outline',
      size: options?.size || 'large',
      text: options?.text || 'signin_with',
      width: 300,
    });
  } catch (error) {
    console.error('Error rendering Google button:', error);
    throw error;
  }
}
